<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  source: string;
  time: string;
  timeTooltip: string;
  message: string;
  user: string;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'source',
      title: 'Source',
    },
    {
      value: 'time',
      title: 'Time',
    },
    {
      value: 'message',
      title: 'Message',
    },
    {
      value: 'user',
      title: 'User',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});
</script>

<template>
  <table
    :class="[tableStyles.table, $style.jobHistoryTable]"
    data-testid="jobHistoryTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText>No history</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr>
          <td :class="$style.sourceTd">
            {{ dataItem.source }}
          </td>
          <td :class="$style.timeTd">
            <span>
              <KParentTooltip>{{ dataItem.timeTooltip }}</KParentTooltip>
              {{ dataItem.time }}
            </span>
          </td>
          <td :class="$style.messageTd">
            {{ dataItem.message }}
          </td>
          <td :class="$style.userTd">
            {{ dataItem.user }}
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobHistoryTable {
  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .sourceTh {
  }
  .timeTh {
  }
  .messageTh {
  }
  .userTh {
  }
  .sourceTd {
  }
  .timeTd {
  }
  .messageTd {
  }
  .userTd {
  }
</style>
